import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, RefreshCw, Mic2 } from "lucide-react";
import { api } from "@/lib/api";
import { StepShell, Field, TInput, TArea, SelectNative, Chips } from "./WizardUI";
import { DJ_PERSONALITIES } from "./constants";

const MODES = [
  { v: "create", l: "Create an AI DJ" },
  { v: "station_voice", l: "Use station voice only" },
  { v: "later", l: "Set this up later" },
];

export default function Step6AIDJ({ saved, aiDj, onSave, onBack, onSkip, busy }) {
  const savedDj = { ...((aiDj || {}).persona || {}), name: aiDj?.name, ...((saved || {}).dj || {}) };
  const [mode, setMode] = useState(saved?.mode || (aiDj ? "create" : ""));
  const [dj, setDj] = useState({
    name: savedDj.name || "", voice_preference: savedDj.voice_preference || "",
    personality: savedDj.personality || "", speaking_style: savedDj.speaking_style || "",
    age_style: savedDj.age_style || "", accent: savedDj.accent || "",
    energy_level: savedDj.energy_level || "medium", humor_level: savedDj.humor_level || "light",
    music_knowledge: savedDj.music_knowledge || "", avoid_topics: savedDj.avoid_topics || "",
  });
  const [intro, setIntro] = useState(saved?.sample_intro || aiDj?.bio || "");
  const [genBusy, setGenBusy] = useState(false);
  const set = (k) => (v) => setDj((x) => ({ ...x, [k]: v }));

  const generate = async () => {
    if (!dj.name.trim()) { toast.error("Give your DJ a name first"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/onboarding/dj/sample-intro", { dj });
      setIntro(data.text);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(false); }
  };

  const submit = () => {
    if (!mode) { toast.error("Pick an option"); return; }
    if (mode === "create" && !dj.name.trim()) { toast.error("Give your AI DJ a name"); return; }
    onSave({ mode, dj, sample_intro: intro });
  };

  return (
    <StepShell step={6} title="AI DJ personality"
      subtitle="An AI DJ gives your station a consistent on-air voice for intros, links, and drops."
      onBack={onBack} onContinue={submit} onSkip={onSkip} busy={busy} canContinue={!!mode}>
      <div className="grid sm:grid-cols-3 gap-2">
        {MODES.map((m) => (
          <button key={m.v} type="button" onClick={() => setMode(m.v)} data-testid={`onb-dj-mode-${m.v}`}
            className={`rounded-md border px-3 py-3 text-sm transition ${
              mode === m.v ? "border-brand-amber/70 bg-brand-amber/10 text-brand-amber"
                           : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"}`}>
            {m.l}
          </button>
        ))}
      </div>
      {mode === "create" && (
        <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="DJ name" required>
              <TInput value={dj.name} onChange={(e) => set("name")(e.target.value)} placeholder="e.g. DJ Velvet" data-testid="onb-dj-name" />
            </Field>
            <Field label="Gender / voice preference">
              <SelectNative options={["Female voice", "Male voice", "Androgynous", "No preference"]}
                value={dj.voice_preference} onChange={set("voice_preference")} placeholder="Choose…" testid="onb-dj-voice" />
            </Field>
          </div>
          <Field label="Personality">
            <Chips options={DJ_PERSONALITIES} values={dj.personality ? [dj.personality] : []} single
              onChange={(v) => set("personality")(v[0] || "")} testidPrefix="onb-dj-pers" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Speaking style">
              <TInput value={dj.speaking_style} onChange={(e) => set("speaking_style")(e.target.value)} placeholder="e.g. slow, intimate, late-night whisper" />
            </Field>
            <Field label="Age style">
              <SelectNative options={["Young & fresh", "Mid-career pro", "Seasoned veteran"]}
                value={dj.age_style} onChange={set("age_style")} placeholder="Choose…" />
            </Field>
            <Field label="Accent preference">
              <TInput value={dj.accent} onChange={(e) => set("accent")(e.target.value)} placeholder="e.g. Jamaican, US urban, neutral" />
            </Field>
            <Field label="Music knowledge style">
              <TInput value={dj.music_knowledge} onChange={(e) => set("music_knowledge")(e.target.value)} placeholder="e.g. deep-cut crate digger, chart expert" />
            </Field>
            <Field label="Energy level">
              <SelectNative options={["low", "medium", "high"]} value={dj.energy_level} onChange={set("energy_level")} />
            </Field>
            <Field label="Humor level">
              <SelectNative options={["none", "light", "heavy"]} value={dj.humor_level} onChange={set("humor_level")} />
            </Field>
          </div>
          <Field label="Topics the DJ should avoid">
            <TInput value={dj.avoid_topics} onChange={(e) => set("avoid_topics")(e.target.value)} placeholder="e.g. politics, religion" />
          </Field>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Sample introduction</div>
              <button type="button" onClick={generate} disabled={genBusy} data-testid="onb-dj-generate-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-1.5 text-[11px] font-semibold text-brand-amber hover:bg-brand-amber/20 transition disabled:opacity-50">
                {genBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : intro ? <RefreshCw className="h-3 w-3" /> : <Wand2 className="h-3 w-3" />}
                {intro ? "Regenerate" : "Generate sample"} · Free during setup
              </button>
            </div>
            <TArea value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="onb-dj-intro"
              placeholder="Click Generate to hear how your DJ would greet listeners — then edit freely." />
          </div>
        </div>
      )}
      {mode === "station_voice" && (
        <div className="rounded-md border border-line bg-bg-elevated/50 px-4 py-3 text-xs text-ink-secondary flex items-center gap-2">
          <Mic2 className="h-4 w-4 text-brand-amber shrink-0" />
          Got it — the AI will speak as "{"the station"}" without a named DJ character. You can add one anytime under AI DJ Assistant.
        </div>
      )}
    </StepShell>
  );
}
